import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";
import { executeRecaptcha, preloadRecaptcha, isRecaptchaEnabled } from "@/lib/recaptcha";

const SERVICE_OPTIONS = [
  "Residential Cleaning",
  "Airbnb Cleaning",
  "Bond Cleaning",
  "Commercial Cleaning",
  "Not sure — please advise",
];

export default function QuoteForm({ compact = false, defaultService = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    service: defaultService || SERVICE_OPTIONS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      const recaptcha_token = await executeRecaptcha("enquiry_submit");
      await api.post("/enquiries", { ...form, recaptcha_token });
      toast.success("Thanks — we've received your enquiry. Our team will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        suburb: "",
        service: defaultService || SERVICE_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please call us on 0416 238 430.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className={`bg-[#17171B] p-8 sm:p-10 relative grain ${
        compact ? "" : "border border-white/10"
      }`}
      data-testid="quote-form"
    >
      <div className="relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label className="field-label" htmlFor="qf-name">Your name</label>
            <input
              id="qf-name"
              className="field-input"
              placeholder="Jane Doe"
              value={form.name}
              onChange={update("name")}
              required
              data-testid="form-input-name"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="qf-email">Email address</label>
            <input
              id="qf-email"
              type="email"
              className="field-input"
              placeholder="jane@example.com"
              value={form.email}
              onChange={update("email")}
              required
              data-testid="form-input-email"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="qf-phone">Phone</label>
            <input
              id="qf-phone"
              className="field-input"
              placeholder="0400 000 000"
              value={form.phone}
              onChange={update("phone")}
              required
              data-testid="form-input-phone"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="qf-suburb">Suburb</label>
            <input
              id="qf-suburb"
              className="field-input"
              placeholder="e.g. Hope Island"
              value={form.suburb}
              onChange={update("suburb")}
              data-testid="form-input-suburb"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="qf-service">Service</label>
            <select
              id="qf-service"
              className="field-input"
              value={form.service}
              onChange={update("service")}
              data-testid="form-input-service"
            >
              {SERVICE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="qf-message">Tell us about the job</label>
            <textarea
              id="qf-message"
              className="field-input"
              placeholder="Property size, preferred date, anything we should know…"
              value={form.message}
              onChange={update("message")}
              required
              data-testid="form-input-message"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary disabled:opacity-70"
            data-testid="form-submit-button"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending
              </>
            ) : (
              <>
                Request my free quote <ArrowRight size={16} />
              </>
            )}
          </button>
          <p className="text-xs text-white/50 tracking-wide">
            We'll respond within 1 business day. No obligation.
          </p>
        </div>
        {isRecaptchaEnabled() && (
          <p className="mt-4 text-[10px] text-white/40 leading-relaxed">
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[#C9A24B]"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[#C9A24B]"
            >
              Terms of Service
            </a>{" "}
            apply.
          </p>
        )}
      </div>
    </form>
  );
}
